/* eslint-disable no-useless-escape */
import React, { ReactElement, type JSX } from 'react'

type HeadingProps = {
  level: number
  children: React.ReactNode
}
const Heading = ({ children, level }: HeadingProps) => {
  if (level <= 0 || level > 5) {
    return <p>{children}</p>
  }
  return [
    <h1 key="h1">{children}</h1>,
    <h2 key="h2">{children}</h2>,
    <h3 key="h3">{children}</h3>,
    <h4 key="h4">{children}</h4>,
    <h5 key="h5">{children}</h5>,
  ][level]
}

type MarkdownParser = {
  (arg0: (string | undefined)[]): JSX.Element
}

// Parsers

// matches the parts of a link, [text:modifier](link)
const linkRegEx = /\[([^\[\]\:]+)(?:\:([a-z]+))?\]\(([^\(\)]+)\)/

const linkParser: MarkdownParser = ([_, text, modifier, link]) => {
  switch (modifier) {
    case 'external':
      return (
        <a href={link} target="_blank" rel="noreferrer">
          {text}
        </a>
      )
    default:
      return <a href={link}>{text}</a>
  }
}

// matches bold text, **text** or __text__
const boldRegEx = /(?:__([^_]+)__|\*\*([^\*]+)\*\*)/

const boldParser: MarkdownParser = ([_, text1, text2]) => {
  const text = text1 ?? text2
  return <strong>{text}</strong>
}

// matches italic text, *text* or _text_
const italicRegEx = /(?:_([^_]+)_|\*([^\*]+)\*)/

const italicParser: MarkdownParser = ([_, text1, text2]) => {
  const text = text1 ?? text2
  return <dfn>{text}</dfn>
}

// matches links, bold and italic
const paragraphPartsRegEx =
  /(\[[^\[\]\:]+(?:\:[a-z]+)?\]\([^\(\)]+\)|_[^_]+_|__[^_]+__|\*[^\*]+\*|\*\*[^\*]+\*\*)/g
// matches paragraphs or headings, # text
const paragraphRegEx = /^(?:(#+)\s)?(.*)$/

const paragraphPartsParsers = [
  [linkRegEx, linkParser],
  [boldRegEx, boldParser],
  [italicRegEx, italicParser],
] as [RegExp, MarkdownParser][]

const paragraphParser: MarkdownParser = ([_, heading, text]) => {
  const line = text?.split(paragraphPartsRegEx).map(str => {
    let parsedStr: JSX.Element | string = str
    for (const [_, [regEx, parser]] of Object.entries(paragraphPartsParsers)) {
      parsedStr = parseText(str, regEx, parser) ?? parsedStr
    }
    return parsedStr
  })

  if (heading) {
    return <Heading level={heading.length}>{line}</Heading>
  }
  return <p>{line}</p>
}

//

const parseText = (text: string, regEx: RegExp, parser: MarkdownParser) => {
  const match = text.match(regEx)
  if (match) {
    return parser(match)
  }
}

export const parseMarkdown = (text: string): ReactElement => {
  // separate on newlines (note that this is the string "\n" and not the new line character)
  const lines = text.split(/\s?(?:\\n)+\s?/g)

  return <>{lines.map(line => parseText(line, paragraphRegEx, paragraphParser))}</>
}
